/**
 * TunerCard — single row in the Tuners list.
 *
 * Notion-strict: name + scope departments on the left, three stat columns
 * on the right (edits / approvals / rejects). Click navigates to the
 * tuner profile route.
 */
import type { TunerSummary } from '@chaos/types/learning';
import { Link } from '@tanstack/react-router';

import { cn } from '../../lib/utils';
import { fmtCount, fmtRelative } from './formatters';

interface TunerCardProps {
  tuner: TunerSummary;
}

export function TunerCard({ tuner }: TunerCardProps) {
  const isActive = tuner.activeSince !== null;
  return (
    <div className="group/tuner relative flex items-center justify-between gap-3 rounded border border-border bg-background px-3 py-2.5 transition-colors hover:bg-muted/40">
      <Link
        to="/university/tuners/$tunerId"
        params={{ tunerId: tuner.tunerId }}
        className="absolute inset-0 z-0 rounded"
        aria-label={`Open tuner ${tuner.displayName}`}
      />
      <div className="relative z-10 flex min-w-0 flex-col">
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-full',
              isActive ? 'bg-success' : 'bg-muted-foreground/40',
            )}
            title={isActive ? 'Active' : 'Inactive'}
          />
          <span className="truncate text-[13px] font-semibold text-foreground">
            {tuner.displayName}
          </span>
        </div>
        <span className="mt-0.5 truncate text-[11px] text-muted-foreground">
          {tuner.scopeDepartmentKeys.length > 0
            ? tuner.scopeDepartmentKeys.join(', ')
            : 'all departments'}
          {' · since '}
          {fmtRelative(tuner.activeSince)}
        </span>
      </div>
      <dl className="relative z-10 flex items-baseline gap-4 text-[11px] text-muted-foreground">
        <Stat label="edits" value={tuner.editsTotal} />
        <Stat label="approvals" value={tuner.approvalsTotal} />
        <Stat label="rejects" value={tuner.rejectedTotal} />
      </dl>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="font-mono text-[13px] font-semibold tabular-nums text-foreground">
        {fmtCount(value)}
      </dd>
    </div>
  );
}
